import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Receipt } from 'lucide-react';
import { receiptCohortCensusAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobErrorBlock } from './JobPollingPanel';

function CountBadge({ count, testId }) {
  return (
    <Badge
      variant={count > 0 ? 'destructive' : 'default'}
      className="text-[10px]"
      data-testid={testId}
    >
      {count}
    </Badge>
  );
}

function SampleTable({ rows, columns }) {
  if (!rows.length) return null;
  return (
    <div className="border rounded mt-1 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            {columns.map((c) => (
              <th key={c.key} className="text-left px-2 py-1 font-medium">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className="border-b last:border-0">
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1 font-mono">{c.render ? c.render(row) : (row[c.key] ?? '-')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReceiptCohortCensusPanel() {
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(null);

  const poll = useJobPolling({
    fetchStatus: receiptCohortCensusAPI.jobStatus,
    onCompleted: useCallback((data) => { setResult(data); setFailed(null); }, []),
    onFailed: useCallback((error) => setFailed(error), []),
  });

  useJobResume({ fetchLatest: useCallback(() => receiptCohortCensusAPI.jobLatest(), []), poll });

  const runNow = async () => {
    setStarting(true);
    setFailed(null);
    try {
      const { data } = await receiptCohortCensusAPI.start();
      if (data.job_id) poll.start(data.job_id);
    } catch (err) {
      setFailed(getErrorMessage(err) || 'Failed to start the census run.');
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || poll.polling;
  const receiptUnpaid = result?.receipt_unpaid;
  const currencySuspect = result?.currency_suspect;
  const chargePairs = result?.charge_pairs;
  const pendingReview = result?.pending_review_reasons;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Receipt Cohort Normalization Census</CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs ml-auto"
            onClick={runNow}
            disabled={busy}
            data-testid="receipt-cohort-census-run"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            {result ? 'Re-run' : 'Run'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Phase-0 census (read-only) for four Gmail-receipt-ingestion defects: receipt-template
          docs left Unpaid, EUR-defaulted currency, invoice/receipt charge-pair duplicates, and
          the pending_review reason distribution locking docs out of the matcher. Run this from
          the browser against the live backend -- sandbox carries none of the real cohort.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {busy && !result && (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
        )}
        <JobErrorBlock error={failed} testId="receipt-cohort-census-error" />

        {result && (
          <>
            <p className="text-xs text-muted-foreground">
              Scanned {result.population_scanned} of {result.total_gmail_population} gmail-source
              docs{result.scan_truncated ? ' (TRUNCATED -- raise the scan cap)' : ''}.
            </p>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-xs font-medium text-muted-foreground">1. Receipt docs left Unpaid</h4>
                <CountBadge count={receiptUnpaid?.total ?? 0} testId="census-receipt-unpaid-total" />
              </div>
              <SampleTable
                rows={receiptUnpaid?.samples || []}
                columns={[
                  { key: 'sender', label: 'Sender' }, { key: 'subject', label: 'Subject' },
                  { key: 'amount', label: 'Amount' }, { key: 'currency', label: 'Ccy' },
                  { key: 'payment_status', label: 'Status' },
                ]}
              />
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-xs font-medium text-muted-foreground">2a. Currency-suspect (tight: body disagrees)</h4>
                <CountBadge count={currencySuspect?.tight_body_disagreement?.total ?? 0} testId="census-currency-tight-total" />
              </div>
              <SampleTable
                rows={currencySuspect?.tight_body_disagreement?.samples || []}
                columns={[
                  { key: 'sender', label: 'Sender' }, { key: 'stored_currency', label: 'Stored' },
                  { key: 'body_detected_currency', label: 'Body says' }, { key: 'amount', label: 'Amount' },
                ]}
              />
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs font-medium text-muted-foreground">2b. Broad pre-fix EUR era (context only)</span>
                <CountBadge count={currencySuspect?.broad_pre_fix_era?.total ?? 0} testId="census-currency-broad-total" />
              </div>
              <p className="text-[10px] text-amber-600">{currencySuspect?.broad_pre_fix_era?.false_positive_risk}</p>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-xs font-medium text-muted-foreground">3. Invoice/receipt charge-pair duplicates</h4>
                <CountBadge count={chargePairs?.total_pairs_found ?? 0} testId="census-charge-pairs-total" />
                <span className="text-[10px] text-muted-foreground">
                  ({chargePairs?.already_linked_by_existing_dedup ?? 0} already linked, {chargePairs?.net_new_pairs ?? 0} net-new)
                </span>
              </div>
              <SampleTable
                rows={(chargePairs?.samples || []).map((p) => ({
                  id: `${p.invoice_doc.id}_${p.receipt_doc.id}`,
                  invoice_id: p.invoice_doc.id, receipt_id: p.receipt_doc.id,
                  amount: p.invoice_doc.amount, linked: p.already_linked_by_existing_dedup ? 'yes' : 'no',
                }))}
                columns={[
                  { key: 'invoice_id', label: 'Invoice doc' }, { key: 'receipt_id', label: 'Receipt doc' },
                  { key: 'amount', label: 'Amount' }, { key: 'linked', label: 'Already linked' },
                ]}
              />
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-xs font-medium text-muted-foreground">4. pending_review reason distribution</h4>
                <CountBadge count={pendingReview?.total_pending_review_gmail ?? 0} testId="census-pending-review-total" />
              </div>
              <div className="text-xs font-mono bg-muted/50 rounded p-1.5 space-y-0.5">
                {Object.entries(pendingReview?.reason_counts || {}).map(([reason, count]) => (
                  <div key={reason}>{reason}: {count}</div>
                ))}
                <div>entity_match_type=unresolved: {pendingReview?.entity_unresolved_total ?? 0}</div>
                <div>regex_fallback (sole reason): {pendingReview?.regex_fallback_sole_reason_total ?? 0}</div>
                <div>empty reasons, reparse outcome set (expected): {pendingReview?.no_reason_with_reparse_outcome ?? 0}</div>
                <div className={pendingReview?.no_reason_unexplained ? 'text-red-600' : ''}>
                  empty reasons, UNEXPLAINED: {pendingReview?.no_reason_unexplained ?? 0}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
